import type * as React from 'react';

import * as api from '@gitbook/api-client';
import { getHiveEndpoint } from '@gitbook/env';
import { createContentComputerFactory, dummyContentComputerStore } from '@gitbook/hive-core';
import { HiveProvider } from '@gitbook/hive-react';
import { type DocumentJSON, deserialize } from '@gitbook/slate';
import { createBrowserCacheEffect } from '@gitbook/util-suspense-cache';

import { apiClient, apiResource, fetchAPICache, liveAPIQuery, useCurrentUser } from '../../state';
import { apiToHive } from './apiToHive';

const hiveTokensBrowserCache = createBrowserCacheEffect('hive-tokens');

/**
 * Authenticate the user using our API to get a token for a given space.
 */
async function getAuthToken(contentId: string): Promise<string[]> {
    const { data } = await apiClient.hive.generateHiveAccessToken({
        contentIds: [contentId],
    });

    return Object.values(data.contents);
}

declare module '@gitbook/hive-core' {
    interface RevisionConfig {
        computedContentSourceRevision: api.ComputedContentSourceRevision;
        computedContentSourceDocument: api.ComputedContentSourceDocument;
    }
}

const contentComputerFactory = createContentComputerFactory({
    context: {
        getSourceVersion: async (_contentId, sourceType) => {
            if (api.checkIsComputedIntegrationSourceType(sourceType)) {
                const parsed = api.parseComputedIntegrationSourceType(sourceType);
                const integration = await fetchAPICache(
                    apiResource('getIntegrationByName', {
                        integrationName: parsed.integration,
                    })
                );

                return integration.version.toString();
            }

            switch (sourceType) {
                case 'builtin:openapi':
                case 'builtin:translation': {
                    return 'builtin';
                }
                default: {
                    // Not throwing an error here as the backend might support other sources
                    // and users might have a cached version of the app.
                    return 'unknown';
                }
            }
        },
        resolveDependency: (spaceId, dependencyRef, onUpdate) => {
            const cleanupCallbacks = new Set<() => void>();

            switch (dependencyRef.kind) {
                case 'space': {
                    cleanupCallbacks.add(
                        liveAPIQuery(
                            apiResource('getSpaceById', {
                                spaceId: dependencyRef.space,
                            }),
                            (space) => {
                                onUpdate({
                                    object: 'space' as const,
                                    id: space.id,
                                    revision: space.revision,
                                });
                            }
                        )
                    );
                    break;
                }
                case 'openapi': {
                    let previousSpace: api.Space | null = null;
                    let cancelOpenAPIQuery: (() => void) | null = null;

                    cleanupCallbacks.add(
                        liveAPIQuery(
                            apiResource('getSpaceById', {
                                spaceId,
                            }),
                            (space) => {
                                if (space.organization === previousSpace?.organization) {
                                    return;
                                }
                                previousSpace = space;
                                if (cancelOpenAPIQuery) {
                                    cancelOpenAPIQuery();
                                    cleanupCallbacks.delete(cancelOpenAPIQuery);
                                }
                                cancelOpenAPIQuery = liveAPIQuery(
                                    apiResource(
                                        'getOpenAPISpecBySlug',
                                        {
                                            organizationId: space.organization,
                                            specSlug: dependencyRef.spec,
                                        },
                                        { nullOnError: true }
                                    ),
                                    (openAPISpec) => {
                                        onUpdate({
                                            object: 'openapi-spec' as const,
                                            id: dependencyRef.spec,
                                            slug: dependencyRef.spec,
                                            lastVersion: openAPISpec?.lastVersion,
                                        });
                                    }
                                );
                                cleanupCallbacks.add(cancelOpenAPIQuery);
                            }
                        )
                    );
                    break;
                }
                case 'translation': {
                    let previousSpace: api.Space | null = null;
                    let cancelTranslationQuery: (() => void) | null = null;

                    cleanupCallbacks.add(
                        liveAPIQuery(
                            apiResource(
                                'getSpaceById',
                                {
                                    spaceId,
                                },
                                { nullOnError: true }
                            ),
                            (space) => {
                                if (space?.organization === previousSpace?.organization) {
                                    return;
                                }
                                previousSpace = space;
                                if (cancelTranslationQuery) {
                                    cancelTranslationQuery();
                                    cleanupCallbacks.delete(cancelTranslationQuery);
                                }

                                if (!space) {
                                    return;
                                }

                                cancelTranslationQuery = liveAPIQuery(
                                    apiResource(
                                        'getTranslation',
                                        {
                                            organizationId: space.organization,
                                            translationId: dependencyRef.translation,
                                        },
                                        { nullOnError: true }
                                    ),
                                    (translation) => {
                                        onUpdate(translation?.result ?? null);
                                    }
                                );
                                cleanupCallbacks.add(cancelTranslationQuery);
                            }
                        )
                    );
                    break;
                }
            }

            return () => {
                cleanupCallbacks.forEach((callback) => callback());
            };
        },
        runComputeRevision: async ({ contentId, computedSeed, computedSource }) => {
            const { data } = await apiClient.spaceContent.getComputedRevision(contentId, {
                source: computedSource,
                seed: computedSeed,
            });

            return {
                ...apiToHive(data),
                contentId,
            };
        },
        runComputeDocument: async ({ contentId, computedSeed, computedSource }) => {
            const { data } = await apiClient.spaceContent.getComputedDocument(contentId, {
                source: computedSource,
                seed: computedSeed,
            });

            return deserialize(data as DocumentJSON);
        },
    },
    store: dummyContentComputerStore,
});

/**
 * Initialize the Hive client.
 */
export function HiveAppProvider(props: { children: React.ReactNode }) {
    const user = useCurrentUser();

    return (
        <HiveProvider
            endpoint={getHiveEndpoint()}
            getAuthToken={getAuthToken}
            tokensBrowserCache={hiveTokensBrowserCache}
            compute={contentComputerFactory}
            presence={
                user
                    ? {
                          userId: user.uid,
                          userDisplayName: user.displayName ?? '',
                      }
                    : undefined
            }
        >
            {props.children}
        </HiveProvider>
    );
}
